import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import DashboardShell from './DashboardShell';
import RoleBadge from '../common/RoleBadge';
import api from '../../services/api';
import { format, formatDistanceToNow } from 'date-fns';

type Tab = 'entities' | 'activity' | 'permissions';
type PermSortKey = 'entity' | 'read' | 'write' | 'delete' | 'execute' | 'admin';
type SortDir = 'asc' | 'desc';

// ── Activity visual config (matches AdminLogs) ──────────────────────────

const actionStyle: Record<string, { dark: string; light: string; icon: string }> = {
  create:         { dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700', icon: '+' },
  role_assign:    { dark: 'bg-purple-900/40 text-purple-300',   light: 'bg-purple-50 text-purple-700',   icon: '\u2194' },
  password_reset: { dark: 'bg-amber-900/40 text-amber-300',     light: 'bg-amber-50 text-amber-700',     icon: '\u26BF' },
  revoke:         { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u2715' },
  delete:         { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u2212' },
  disable:        { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u25CB' },
  enable:         { dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700', icon: '\u25CF' },
  update:         { dark: 'bg-blue-900/40 text-blue-300',       light: 'bg-blue-50 text-blue-700',       icon: '\u270E' },
};
const defaultActionStyle = { dark: 'bg-gray-700 text-gray-300', light: 'bg-gray-100 text-gray-600', icon: '\u2022' };

const resourceIcons: Record<string, string> = {
  user: '\u{1F464}', persona: '\u{1F916}', invite: '\u2709', template: '\u{1F4CB}',
  session: '\u{1F4AC}', artifact: '\u{1F4E6}', workflow: '\u2699',
};

const rowAccent: Record<string, string> = {
  create: 'border-l-2 border-emerald-500/40', role_assign: 'border-l-2 border-purple-500/40',
  password_reset: 'border-l-2 border-amber-500/40', delete: 'border-l-2 border-red-500/40',
  revoke: 'border-l-2 border-red-500/40', disable: 'border-l-2 border-red-500/40',
  enable: 'border-l-2 border-emerald-500/40', update: 'border-l-2 border-blue-500/40',
};

function summarizeActivity(item: ActivityItem): string {
  const d = item.details;
  if (!d) return item.resource_id?.substring(0, 8) || '\u2014';
  if (item.action === 'role_assign') return `${d.previous_role || '?'} \u2192 ${d.role}`;
  if (item.action === 'create' && d.email) return d.email;
  if (item.action === 'create' && d.name) return d.name;
  if (item.action === 'password_reset') return d.method || 'admin triggered';
  if (d.username) return d.username;
  if (d.via === 'admin_chat') return 'via Admin Chat';
  return item.resource_id?.substring(0, 8) || '\u2014';
}

interface ProfileData {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role?: string;
  auth_provider?: string;
  created_at?: string;
  last_login?: string;
  entity_counts: Record<string, number>;
  permissions: string[];
}

interface ActivityItem {
  id?: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details?: any;
  timestamp: string;
}

const ENTITY_TYPES = ['personas', 'sessions', 'artifacts', 'rosters', 'teams', 'committees', 'gauntlets'];

function getDisplayName(p: ProfileData): string {
  return p.full_name || p.username || (p.email ? p.email.split('@')[0] : 'Unknown');
}

function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
}

// Parse "personas.read" → { entity: "personas", action: "read" }
function parsePermission(perm: string): { entity: string; action: string } | null {
  const dot = perm.indexOf('.');
  if (dot < 0) return null;
  return { entity: perm.substring(0, dot), action: perm.substring(dot + 1) };
}

const UserDashboard: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { darkMode } = useTheme();
  const { user: currentUser } = useAuth();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('entities');

  // Entities tab
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [entityItems, setEntityItems] = useState<any[]>([]);
  const [entityLoading, setEntityLoading] = useState(false);

  // Activity tab
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);

  // Permissions tab sorting
  const [permSortKey, setPermSortKey] = useState<PermSortKey>('entity');
  const [permSortDir, setPermSortDir] = useState<SortDir>('asc');

  const isOwnProfile = currentUser && userId && String(currentUser.id) === String(userId);
  const displayName = profile ? getDisplayName(profile) : '';

  // Fetch profile
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/users/${userId}/profile`)
      .then(r => { setProfile(r.data); setError(null); })
      .catch(e => setError(e.response?.data?.detail || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [userId]);

  // Fetch entities when type selected
  useEffect(() => {
    if (!userId || !selectedType) return;
    setEntityLoading(true);
    api.get(`/users/${userId}/entities`, { params: { entity_type: selectedType, limit: 50 } })
      .then(r => setEntityItems(r.data.items || []))
      .catch(() => setEntityItems([]))
      .finally(() => setEntityLoading(false));
  }, [userId, selectedType]);

  // Fetch activity when tab selected
  useEffect(() => {
    if (!userId || activeTab !== 'activity') return;
    setActivityLoading(true);
    api.get(`/users/${userId}/activity`, { params: { limit: 50 } })
      .then(r => { setActivity(r.data.items || []); setActivityTotal(r.data.total || 0); })
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [userId, activeTab]);

  if (loading) {
    return (
      <DashboardShell title={isOwnProfile ? 'My Dashboard' : 'User Dashboard'}>
        {() => (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500"></div>
          </div>
        )}
      </DashboardShell>
    );
  }

  if (error || !profile) {
    return (
      <DashboardShell title="User Dashboard">
        {() => (
          <div className={`rounded-xl p-6 ${darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'}`}>
            {error || 'Profile not found'}
          </div>
        )}
      </DashboardShell>
    );
  }

  // Build permissions grid data
  const permGrid: Record<string, Set<string>> = {};
  const allActions = new Set<string>();
  for (const perm of profile.permissions) {
    const parsed = parsePermission(perm);
    if (!parsed) continue;
    if (!permGrid[parsed.entity]) permGrid[parsed.entity] = new Set();
    permGrid[parsed.entity].add(parsed.action);
    allActions.add(parsed.action);
  }
  const actionCols = ['read', 'write', 'delete', 'execute', 'admin'].filter(a => allActions.has(a));
  const entityRows = Object.keys(permGrid).sort();

  const tabClass = (tab: Tab) => `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
    activeTab === tab
      ? darkMode ? 'border-sky-500 text-sky-400' : 'border-sky-500 text-sky-600'
      : darkMode ? 'border-transparent text-gray-500 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'
  }`;

  return (
    <DashboardShell title={isOwnProfile ? 'My Dashboard' : `${displayName}'s Profile`}>
      {(_dateRange) => (
        <div className="space-y-6">
          {/* Identity Strip */}
          <div className={`flex items-center gap-4 p-4 rounded-xl border-l-4 border-sky-500 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={displayName} className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className={`h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                {getInitials(displayName)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{displayName}</h2>
                {profile.role && <RoleBadge role={profile.role} size="sm" />}
                {profile.auth_provider && (
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{profile.auth_provider}</span>
                )}
              </div>
              <div className={`text-xs flex items-center gap-3 mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <span>{profile.email}</span>
                {profile.created_at && <span>Member since {new Date(profile.created_at).toLocaleDateString()}</span>}
              </div>
            </div>
            {!isOwnProfile && (
              <a href="/admin/users" target="_blank" rel="noopener noreferrer"
                className={`text-xs px-3 py-1.5 rounded-lg shrink-0 ${darkMode ? 'bg-gray-700 text-gray-400 hover:text-gray-200' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`}>
                Edit in Admin
              </a>
            )}
            {isOwnProfile && (
              <a href="/profile"
                className={`text-xs px-3 py-1.5 rounded-lg shrink-0 ${darkMode ? 'bg-gray-700 text-gray-400 hover:text-gray-200' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`}>
                Account Settings
              </a>
            )}
          </div>

          {/* Tabs */}
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="flex">
              <button onClick={() => setActiveTab('entities')} className={tabClass('entities')}>Entities</button>
              <button onClick={() => setActiveTab('activity')} className={tabClass('activity')}>Activity</button>
              <button onClick={() => setActiveTab('permissions')} className={tabClass('permissions')}>Permissions</button>
            </nav>
          </div>

          {/* ── Entities Tab ── */}
          {activeTab === 'entities' && (
            <div>
              {/* Count pills */}
              <div className="flex flex-wrap gap-2 mb-4">
                {ENTITY_TYPES.map(type => {
                  const count = profile.entity_counts[type] ?? 0;
                  const active = selectedType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(active ? null : type)}
                      className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                        active
                          ? darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-50 text-sky-700'
                          : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {type} {count}
                    </button>
                  );
                })}
              </div>

              {/* Entity list */}
              {selectedType && (
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                  {entityLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-sky-500"></div>
                    </div>
                  ) : entityItems.length === 0 ? (
                    <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No {selectedType} found.</p>
                  ) : (
                    <div className={`divide-y ${darkMode ? 'divide-gray-700/30' : 'divide-gray-100'}`}>
                      {entityItems.map((item: any) => (
                        <div key={item.id} className={`py-2 px-1 flex justify-between items-center ${darkMode ? 'hover:bg-gray-700/20' : 'hover:bg-gray-50'}`}>
                          <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.name}</span>
                          <span className={`text-[10px] font-mono ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>{item.id?.substring(0, 8)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!selectedType && (
                <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Click an entity type above to browse.
                </p>
              )}
            </div>
          )}

          {/* ── Activity Tab ── */}
          {activeTab === 'activity' && (
            <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
              {activityLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-sky-500"></div>
                </div>
              ) : activity.length === 0 ? (
                <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No activity recorded.</p>
              ) : (
                <>
                  <table className="w-full">
                    <thead>
                      <tr className={darkMode ? 'bg-gray-900/40' : 'bg-gray-50'}>
                        {['Time', 'Action', 'Resource', 'Summary'].map(h => (
                          <th key={h} className={`px-4 py-2.5 text-left text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700/30' : 'divide-gray-100'}`}>
                      {activity.map((item, i) => {
                        const as = actionStyle[item.action] || defaultActionStyle;
                        const accent = rowAccent[item.action] || 'border-l-2 border-transparent';
                        const ri = resourceIcons[item.resource_type] || '\u2022';
                        const hasChatBadge = item.details?.via === 'admin_chat';

                        return (
                          <tr key={item.id || i} className={`${accent} ${darkMode ? 'hover:bg-gray-700/20' : 'hover:bg-gray-50'}`}>
                            <td className="px-4 py-2.5 align-top w-[130px]">
                              <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {item.timestamp ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true }) : ''}
                              </div>
                              <div className={`text-[10px] font-mono ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                {item.timestamp ? format(new Date(item.timestamp), 'MMM d, HH:mm') : ''}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 align-top w-[110px]">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${darkMode ? as.dark : as.light}`}>
                                <span className="text-[11px]">{as.icon}</span>
                                {item.action.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 align-top w-[90px]">
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span className="mr-1">{ri}</span>{item.resource_type}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 align-top">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                  {summarizeActivity(item)}
                                </span>
                                {hasChatBadge && (
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-500'}`}>AI</span>
                                )}
                              </div>
                              <div className={`text-[10px] font-mono mt-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                {item.resource_id?.substring(0, 12)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className={`px-4 py-2.5 border-t text-xs ${darkMode ? 'border-gray-700/30 text-gray-600' : 'border-gray-100 text-gray-400'}`}>
                    {activityTotal} total entries
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Permissions Tab ── */}
          {activeTab === 'permissions' && (() => {
            const togglePermSort = (key: PermSortKey) => {
              if (permSortKey === key) setPermSortDir(d => d === 'asc' ? 'desc' : 'asc');
              else { setPermSortKey(key); setPermSortDir('asc'); }
            };
            const sortArrow = (key: PermSortKey) => {
              if (permSortKey !== key) return <span className="opacity-30 ml-0.5">{'\u2195'}</span>;
              return <span className="ml-0.5">{permSortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
            };
            const sortedRows = [...entityRows].sort((a, b) => {
              let cmp = 0;
              if (permSortKey === 'entity') {
                cmp = a.localeCompare(b);
              } else {
                const aHas = permGrid[a]?.has(permSortKey) ? 1 : 0;
                const bHas = permGrid[b]?.has(permSortKey) ? 1 : 0;
                cmp = aHas - bHas;
              }
              return permSortDir === 'asc' ? cmp : -cmp;
            });
            const permCount = profile.permissions.length;
            const grantedCount = Object.values(permGrid).reduce((sum, s) => sum + s.size, 0);

            return (
              <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                {entityRows.length === 0 ? (
                  <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No permissions assigned.</p>
                ) : (
                  <>
                    <table className="w-full">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-900/40' : 'bg-gray-50'}>
                          <th
                            className={`px-4 py-2.5 text-left text-[10px] uppercase tracking-wider font-semibold cursor-pointer select-none ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => togglePermSort('entity')}
                          >
                            Entity {sortArrow('entity')}
                          </th>
                          {actionCols.map(a => (
                            <th
                              key={a}
                              className={`px-3 py-2.5 text-center text-[10px] uppercase tracking-wider font-semibold cursor-pointer select-none ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                              onClick={() => togglePermSort(a as PermSortKey)}
                            >
                              {a} {sortArrow(a as PermSortKey)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-700/30' : 'divide-gray-100'}`}>
                        {sortedRows.map(entity => {
                          const granted = actionCols.filter(a => permGrid[entity]?.has(a)).length;
                          return (
                            <tr key={entity} className={darkMode ? 'hover:bg-gray-700/20' : 'hover:bg-gray-50'}>
                              <td className="px-4 py-2">
                                <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{entity}</div>
                                <div className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                  {granted}/{actionCols.length} granted
                                </div>
                              </td>
                              {actionCols.map(action => (
                                <td key={action} className="px-3 py-2 text-center">
                                  {permGrid[entity]?.has(action) ? (
                                    <span className={`text-sm ${darkMode ? 'text-sky-400' : 'text-sky-600'}`}>&#10003;</span>
                                  ) : (
                                    <span className={`text-sm ${darkMode ? 'text-gray-700' : 'text-gray-300'}`}>&mdash;</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className={`px-4 py-2.5 border-t text-xs ${darkMode ? 'border-gray-700/30 text-gray-600' : 'border-gray-100 text-gray-400'}`}>
                      {permCount} permissions across {entityRows.length} entity types ({grantedCount} grants)
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </DashboardShell>
  );
};

export default UserDashboard;
